"use client";

import { useState, useCallback } from "react";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/contexts/LanguageContext";
import { useJsonTranslation } from "@/hooks/useJsonTranslation";
import { Sparkles, ArrowUpRight, Bug, Loader2 } from "lucide-react";
import { reportClientError } from "@/lib/observability/report-client-error";

type ChangeType = "feature" | "improvement" | "fix";

interface ChangelogChange {
  type: ChangeType;
  text: string;
}

export interface ChangelogEntry {
  id: string;
  version: string;
  title: string;
  summary: string;
  changes: ChangelogChange[];
  created_at: string;
}

interface ChangelogTimelineProps {
  initialEntries: ChangelogEntry[];
  initialTotal: number;
  initialPage: number;
  initialLocale: string;
}

function formatDate(isoString: string, language: string): string {
  try {
    const date = new Date(isoString);
    if (Number.isNaN(date.getTime())) return isoString;
    return new Intl.DateTimeFormat(language, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }).format(date);
  } catch (err) {
    reportClientError(err, {
      source: "components.ChangelogTimeline",
      feature: "changelog",
      action: "formatDate",
      severity: "warning",
    });
    return isoString;
  }
}

function ChangeIcon({ type }: { type: ChangeType }) {
  switch (type) {
    case "feature":
      return (
        <Sparkles
          className="text-leaf-600 dark:text-leaf-400 h-4 w-4 shrink-0"
          aria-hidden="true"
        />
      );
    case "improvement":
      return (
        <ArrowUpRight
          className="text-leaf-400 h-4 w-4 shrink-0"
          aria-hidden="true"
        />
      );
    case "fix":
      return (
        <Bug className="text-leaf-400 h-4 w-4 shrink-0" aria-hidden="true" />
      );
  }
}

function ChangeBadge({ type, label }: { type: ChangeType; label: string }) {
  const classes: Record<ChangeType, string> = {
    feature: "bg-leaf-500/20 text-leaf-600 dark:text-leaf-400",
    improvement: "bg-leaf-500/20 text-leaf-400",
    fix: "bg-leaf-500/20 text-leaf-400",
  };
  return <Badge className={classes[type]}>{label}</Badge>;
}

export default function ChangelogTimeline({
  initialEntries,
  initialTotal,
  initialPage,
  initialLocale,
}: ChangelogTimelineProps) {
  const { t } = useJsonTranslation("ChangelogPage");
  const { language } = useLanguage();
  const [entries, setEntries] = useState(initialEntries);
  const [total, setTotal] = useState(initialTotal);
  const [page, setPage] = useState(initialPage);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const lang = language || initialLocale;
  const hasMore = entries.length < total;

  const loadMore = useCallback(async () => {
    setLoading(true);
    setError(false);
    const nextPage = page + 1;
    const url = `/api/changelog/?page=${nextPage}&limit=10&lang=${lang}`;
    try {
      let res: Response | null = null;
      for (let attempt = 0; attempt < 4; attempt++) {
        res = await fetch(url, { credentials: "include" });
        if (res.status !== 429) {
          break;
        }
        const ra = parseInt(res.headers.get("Retry-After") || "3", 10);
        await new Promise((r) =>
          setTimeout(r, Math.min(30_000, Math.max(1000, ra * 1000)))
        );
      }
      if (!res || !res.ok) {
        throw new Error(res ? `HTTP ${res.status}` : "Changelog fetch failed");
      }
      const data = await res.json();
      setEntries((prev) => [...prev, ...data.entries]);
      setTotal(data.total);
      setPage(nextPage);
    } catch (err) {
      setError(true);
      reportClientError(err, {
        source: "pages.changelog",
        feature: "changelog",
        action: "loadMore",
      });
    } finally {
      setLoading(false);
    }
  }, [page, lang]);

  const groupChanges = (changes: ChangelogChange[]) => {
    const groups: Record<ChangeType, ChangelogChange[]> = {
      feature: [],
      improvement: [],
      fix: [],
    };
    for (const c of changes) {
      if (groups[c.type]) groups[c.type].push(c);
    }
    return groups;
  };

  const badgeLabels: Record<ChangeType, string> = {
    feature: t("badges.new"),
    improvement: t("badges.improved"),
    fix: t("badges.fixed"),
  };

  return (
    <div>
      <div className="relative" aria-live="polite">
        {/* Vertical line */}
        <div className="border-border absolute top-0 bottom-0 left-3 w-px border-l md:left-4" />

        <div className="space-y-8">
          {entries.map((entry, idx) => {
            const grouped = groupChanges(entry.changes);
            return (
              <div key={entry.id} className="relative pl-10 md:pl-14">
                {/* Timeline dot */}
                <div
                  className={`border-background absolute top-1 left-1.5 h-4 w-4 rounded-full border-2 md:left-2.5 ${
                    idx === 0 ? "bg-leaf-500" : "bg-muted-foreground/30"
                  }`}
                />

                {/* Version + timestamp */}
                <div className="mb-2 flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-3">
                  <div className="flex items-center gap-2">
                    <Badge
                      variant="outline"
                      className="border-border font-mono text-xs tabular-nums"
                    >
                      v{entry.version}
                    </Badge>
                    {idx === 0 && (
                      <Badge className="bg-leaf-500/20 text-leaf-500 text-xs">
                        {t("latest")}
                      </Badge>
                    )}
                  </div>
                  <span className="text-muted-foreground text-xs tabular-nums">
                    {formatDate(entry.created_at, lang)}
                  </span>
                </div>

                {/* Card */}
                <div className="border-border glass rounded-2xl p-4 md:p-5">
                  <h3
                    className="text-foreground mb-2 text-lg font-semibold"
                    style={{ textWrap: "balance" }}
                  >
                    {entry.title}
                  </h3>
                  <p className="text-muted-foreground mb-4 text-sm leading-relaxed">
                    {entry.summary}
                  </p>

                  <div className="space-y-3">
                    {(["feature", "improvement", "fix"] as const).map(
                      (type) =>
                        grouped[type].length > 0 && (
                          <div key={type}>
                            <div className="mb-1.5 flex items-center gap-2">
                              <ChangeIcon type={type} />
                              <ChangeBadge
                                type={type}
                                label={badgeLabels[type]}
                              />
                            </div>
                            <ul className="space-y-1 pl-6">
                              {grouped[type].map((c, ci) => (
                                <li
                                  key={ci}
                                  className="text-muted-foreground list-disc text-sm"
                                >
                                  {c.text}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-10 text-center">
        {entries.length === 0 && (
          <p className="text-muted-foreground text-sm">{t("empty")}</p>
        )}
        {error && <p className="text-leaf-400 mb-4 text-sm">{t("error")}</p>}
        {hasMore && (
          <button
            onClick={loadMore}
            disabled={loading}
            className="text-foreground border-border hover:bg-muted focus-visible:ring-ring inline-flex items-center gap-2 rounded-lg border px-6 py-2.5 text-sm font-medium transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:opacity-50"
            style={{ touchAction: "manipulation" }}
            aria-label="Load more changelog updates"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                {t("loading")}
              </>
            ) : (
              t("loadMore")
            )}
          </button>
        )}
      </div>
    </div>
  );
}
